import useModal from 'hooks/useModal';
import {
  ProviderProps,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import createNamedContext from 'utils/createNamedContext';
import KnowledgeListFormModal, { KnowledgeListFormModalProps } from './KnowledgeListFormModal';
import useEdit from '../edit/useEdit';
import EditDrawer from '../edit/EditDrawer';
import {
  batchUpdateKnowledgeContent,
  getCommonKnowledges,
  getStoreGroupList,
  logisticsKnowledgeRecreate,
} from '@knowledge/service/api/general';
import { message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { typeValue } from '../KnowledgeListForm/KnowledgeType';
import { ActivityContextProvider } from '@knowledge/Activity/ActivityContext';
import {
  getStoreMap,
  isNeedSliceStoreKnowledgeType,
  supportKnowledgeStoresFilter,
  useInitialValues,
  useTransformKnowledges,
} from '../utils';
import { useStores } from '@knowledge/common/cache/store';
import { UnConfigStore } from 'views/BootGuide/views/AiModel/share';
import { useRequest } from 'ahooks';
import { storeGroupList2withScopes } from '../StoreGroup/share';
import { logisticsKnowledgeTypes, useLLM } from '@knowledge/common/hooks/useGroup';

type KnowledgeType = Knowledge.KnowledgeType;

type KnowledgeMap = {
  [K in KnowledgeType]?: Knowledge.Value.Value<K>[];
};

type KnowledgeExampleMap = Partial<Record<KnowledgeType, ReactNode>>;

export interface KnowledgeContextValue {
  knowledgeMap: KnowledgeMap;
  knowledgeExampleMap: KnowledgeExampleMap;
  showKnowledgeListMoal: (type: KnowledgeType) => void;
  getKnowledgeExample: (type: KnowledgeType) => void;
}

export const KnowledgeContext = createNamedContext<KnowledgeContextValue | null>(
  'KnowledgeContext',
  null,
);

export function useKnowledgeContext() {
  const value = useContext(KnowledgeContext);

  if (value === null) {
    throw new Error(
      'useKnowledgeContext should be used in a context of KnowledgeContextProvider component',
    );
  }

  return value;
}

export interface KnowledgeContextProviderProps
  extends Omit<ProviderProps<KnowledgeContextValue>, 'value'> {
  onUpdate?: (type: KnowledgeType) => void;
}

export function KnowledgeContextProvider(props: KnowledgeContextProviderProps) {
  const { children, onUpdate, ...otherProps } = props;

  const { t } = useTranslate();

  const [knowledgeMap, setKnowledgeMap] = useState<KnowledgeMap>({});

  const knowledgeLoadingMap = useRef<Partial<Record<KnowledgeType, boolean>>>({});

  const { visible, show, hide, meta } = useModal<KnowledgeType>();

  const [openDrawer] = useEdit();

  const drawer = meta && <EditDrawer type={meta} />;

  const fetch = useCallback(
    (type: KnowledgeType, force = false) => {
      const { [type]: loading } = knowledgeLoadingMap.current;
      const { [type]: knowledgeList } = knowledgeMap;

      if (!force && (loading || knowledgeList)) {
        return;
      }

      knowledgeLoadingMap.current = {
        ...knowledgeLoadingMap.current,
        [type]: true,
      };

      getCommonKnowledges(type)
        .then((res) => {
          setKnowledgeMap((pre) => {
            return {
              ...pre,
              [type]: res,
            };
          });
        })
        .catch(() => {
          message.error(t('phrase.获取数据失败，请稍后再试'));
        })
        .finally(() => {
          knowledgeLoadingMap.current = {
            ...knowledgeLoadingMap.current,
            [type]: false,
          };
        });
    },
    [knowledgeMap, t],
  );

  const onKnowledgeUpdate = useCallback(() => {
    if (meta) {
      onUpdate && onUpdate(meta);
      fetch(meta, true);
    }
  }, [meta, onUpdate, fetch]);

  const LLM = useLLM();

  const transformKnowledges = useTransformKnowledges({ type: meta as any });

  const modal = meta ? (
    <KnowledgeListFormModalWithInitialValues
      visible={visible}
      knowledgeType={meta}
      knowledgeMap={knowledgeMap}
      onCancel={() => hide()}
      onSubmit={(values) => {
        if (
          logisticsKnowledgeTypes.includes(meta) &&
          LLM &&
          meta !== 'LOGISTICS_DELIVERY_COURIER_TYPE'
        ) {
          return logisticsKnowledgeRecreate({
            type: 'UPDATE',
            knowledges: (values.kc as any[]).reduce<any[]>((res, { storeGroup, knowledges }) => {
              return [
                ...res,
                ...transformKnowledges(knowledges).map((knowledge) => {
                  return {
                    ...knowledge,
                    groupId: storeGroup.id,
                  };
                }),
              ];
            }, []),
          })
            .then(() => {
              message.success(t('operation.saveSucceed'));

              hide();

              onKnowledgeUpdate();
            })
            .catch(() => {
              message.error(t('operation.saveFailed'));
            });
        }

        const items = values?.kc
          ?.map((each) => {
            const { conditions, content, templateCode } = each;
            return conditions.map((c) => {
              return {
                id: c.id,
                content,
                templateCode,
              };
            });
          })
          ?.flat();

        return batchUpdateKnowledgeContent({ items })
          .then(() => {
            message.success(t('operation.saveSucceed'));

            hide();

            onKnowledgeUpdate();
          })
          .catch(() => {
            message.error(t('operation.saveFailed'));
          });
      }}
      onEdit={(data) =>
        openDrawer({
          data,
          refreshFn: onKnowledgeUpdate,
        })
      }
      onUpdate={onKnowledgeUpdate}
    />
  ) : null;

  const showKnowledgeListMoal = useCallback(
    (type: KnowledgeType) => {
      show(type);
      fetch(type);
    },
    [show, fetch],
  );

  const knowledgeExampleMap = useMemo(() => {
    return Object.keys(knowledgeMap).reduce<KnowledgeExampleMap>((res, cur) => {
      const { [cur]: knowledgeList = [] } = knowledgeMap;

      const [knowledge] = knowledgeList;

      if (!knowledge) {
        return res;
      }

      const { definitionCode, templateCode, content } = knowledge;

      res[cur] = typeValue(t, definitionCode, templateCode, content);

      return res;
    }, {});
  }, [knowledgeMap, t]);

  const getKnowledgeExample = useCallback(
    (type: KnowledgeType) => {
      const { [type]: knowledgeList } = knowledgeMap;
      const { [type]: example } = knowledgeExampleMap;

      fetch(type);

      return knowledgeList ? example : false;
    },
    [knowledgeMap, knowledgeExampleMap, fetch],
  );

  const value = useMemo<KnowledgeContextValue>(
    () => ({
      knowledgeMap,
      knowledgeExampleMap,
      showKnowledgeListMoal,
      getKnowledgeExample,
    }),
    [knowledgeMap, knowledgeExampleMap, showKnowledgeListMoal, getKnowledgeExample],
  );

  return (
    <KnowledgeContext.Provider value={value} {...otherProps}>
      <ActivityContextProvider>
        {children}
        {drawer}
        {modal}
      </ActivityContextProvider>
    </KnowledgeContext.Provider>
  );
}

function KnowledgeListFormModalWithInitialValues<T extends Knowledge.KnowledgeType>({
  knowledgeType,
  knowledgeMap,
  ...otherProps
}: Omit<
  KnowledgeListFormModalProps<T>,
  'initialValues' | 'isShowLogisticsFormContent' | 'stores'
> & {
  knowledgeMap: KnowledgeMap;
}) {
  const { data: _stores } = useStores();
  const stores = useMemo(
    () => supportKnowledgeStoresFilter((_stores as undefined | UnConfigStore[]) || []),
    [_stores],
  );
  const storeMap = useMemo(() => getStoreMap(stores || []), [stores]);
  const { data: storeGroupList } = useRequest(getStoreGroupList);

  const storeGroupWithScopesList = useMemo(() => {
    return storeGroupList2withScopes(storeGroupList || [], storeMap);
  }, [storeGroupList, storeMap]);

  const { LLM, getInitialValues } = useInitialValues();
  const initialValues = useMemo(
    () =>
      getInitialValues({
        type: knowledgeType,
        knowledgeValues: knowledgeMap[knowledgeType],
        storeGroupList: storeGroupWithScopesList,
        storeMap,
      }),
    [knowledgeType, knowledgeMap, storeGroupWithScopesList, storeMap, getInitialValues],
  );
  const isShowLogisticsFormContent = LLM && isNeedSliceStoreKnowledgeType(knowledgeType);

  if (!stores || !storeGroupList) {
    return <></>;
  }

  return (
    <KnowledgeListFormModal
      {...otherProps}
      knowledgeType={knowledgeType}
      initialValues={initialValues}
      isShowLogisticsFormContent={isShowLogisticsFormContent}
      stores={stores}
    />
  );
}
